import cn from "classnames";
import { useEffect, useCallback, useState, useRef } from "react";

// components
import { Button, Input } from "components/LibraryComponents/Components";
import { Saved } from "components/EditBusinessProfileSections/shared/Saved/Saved";
import { ResponsiveStack } from "components/ResponsiveStack/ResponsiveStack";

// types
import { ChangesProps } from "../ServiceSection";

// hooks 
import { useMeiliTaskToast } from "hooks/useMeiliTaskToast";
import { useSaveConfirmation } from "hooks/useSaveConfirmation";

// utils
import { saveErrorToast } from "utils/edit-profile";

// store
import { useProfileEditStore } from "store/useProfileEditStore";

// styles
import styles from "../ServiceSection.module.scss";

// fetch
import { updateExtendedProfileData } from "data/fetch";

type Props = {
  setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  saveConfirmation: ReturnType<typeof useSaveConfirmation>;
  section: string;
};

export default function BondingLevelSection({ setUnsavedChanges, saveConfirmation, section }: Props) {
  const { extendedProfile, setExtendedProfile } = useProfileEditStore(
    (state) => ({
      extendedProfile: state.extendedProfile,
      setExtendedProfile: state.setExtendedProfile,
    })
  );

  const [formData, setFormData] = useState({
    constructionIndividual: "",
    constructionAggregate: "",
    serviceIndividual: "",
    serviceAggregate: "",
  });
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const isInitialized = useRef(false); // Track if formData has been initialized
  const initialFormData = useRef({
    constructionIndividual: "",
    constructionAggregate: "",
    serviceIndividual: "",
    serviceAggregate: "",
  }); // Store initial bonding_levels for change detection

  const { 
    lastSavedSection,
    showConfirmation,
    clearConfirmation
  } = saveConfirmation;

  const addTask = useMeiliTaskToast();

  // Parse bonding_levels string to object
  const parseBondingLevels = (bondingLevels: string | null) => {
    const result = {
      constructionIndividual: "",
      constructionAggregate: "",
      serviceIndividual: "",
      serviceAggregate: "",
    };
    if (!bondingLevels) return result;

    const parts = bondingLevels.split(";").reduce((acc, part) => {
      const [key, value] = part.split(":");
      if (key && value) {
        if (key === "ConstPer") acc.constructionIndividual = value;
        if (key === "ConstAgg") acc.constructionAggregate = value;
        if (key === "ServPer") acc.serviceIndividual = value;
        if (key === "ServAgg") acc.serviceAggregate = value;
      }
      return acc;
    }, result);

    return result;
  };

  // Convert formData to bonding_levels string
  const formatBondingLevels = (data: typeof formData) => {
    return [
      `ConstPer:${data.constructionIndividual || "0"}`,
      `ConstAgg:${data.constructionAggregate || "0"}`,
      `ServPer:${data.serviceIndividual || "0"}`,
      `ServAgg:${data.serviceAggregate || "0"}`,
    ].join(";");
  };

  // Check if formData has changed
  const hasFieldChanged = useCallback(() =>
    formatBondingLevels(formData) !==
    formatBondingLevels(initialFormData.current), [formData, initialFormData]);

  // Check if formData is empty
  const isFormEmpty = Object.values(formData).every((value) => value === "");

  // Update unsaved changes state
  useEffect(() => {
    setUnsavedChanges(hasFieldChanged());
    clearConfirmation()
    return () => {
      setUnsavedChanges(false);
    };
  }, [formData, setUnsavedChanges]);

  // Initialize formData from extendedProfile
  useEffect(() => {
    if (extendedProfile && !isInitialized.current && isFormEmpty) {
      const initial = parseBondingLevels(extendedProfile.bonding_levels);
      setFormData(initial);
      initialFormData.current = initial;
      isInitialized.current = true;
    }
  }, [extendedProfile, isFormEmpty]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Allow only non-negative numbers
    if (value === "" || (/^\d+$/.test(value) && parseInt(value) >= 0)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSaveField = async () => {
    if (!hasFieldChanged() || isSaving || isFormEmpty) return;

    setIsSaving(true);
    try {
      const bondingLevels = formatBondingLevels(formData);

      const response = await updateExtendedProfileData(
        extendedProfile.uei,
        JSON.stringify({
          bonding_levels: bondingLevels,
        })
      );

      // Simulate network delay
      // await new Promise((resolve) => setTimeout(resolve, 1000));

      setExtendedProfile({
        ...extendedProfile,
        bonding_levels: bondingLevels,
      });

      const responseData = await response.json();
      addTask({
        taskUid: responseData?.taskUid,
        label: section,
        status: responseData?.status,
      });
      showConfirmation(section);

      initialFormData.current = { ...formData }; // Update initial values after save
    } catch (error: any) {
      console.error(`Error saving bonding levels:`, error);
      saveErrorToast();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn(styles["section-content"], styles["space-y-6"])}>
      <div>
        <h3 className={styles["title"]}>Bonding levels</h3>
        <p className={styles["desc"]}>Write your bonding levels in USD.</p>
      </div>

      <div className={styles["space-y-4"]}>
        <Input
          id="constructionIndividual"
          name="constructionIndividual"
          label="Minimum construction bonding level: Individual"
          type="number"
          rootClassName={styles["input-root-wrapper"]}
          inputClassName={styles["input-number"]}
          value={formData.constructionIndividual}
          onChange={handleChange}
          min="0"
        />

        <Input
          id="constructionAggregate"
          name="constructionAggregate"
          label="Minimum construction bonding level: Aggregate"
          type="number"
          rootClassName={styles["input-root-wrapper"]}
          inputClassName={styles["input-number"]}
          value={formData.constructionAggregate}
          onChange={handleChange}
          min="0"
        />

        <Input
          id="serviceIndividual"
          name="serviceIndividual"
          label="Minimum service bonding level: Individual"
          type="number"
          rootClassName={styles["input-root-wrapper"]}
          inputClassName={styles["input-number"]}
          value={formData.serviceIndividual}
          onChange={handleChange}
          min="0"
        />

        <Input
          id="serviceAggregate"
          name="serviceAggregate"
          label="Minimum service bonding level: Aggregate"
          type="number"
          rootClassName={styles["input-root-wrapper"]}
          inputClassName={styles["input-number"]}
          value={formData.serviceAggregate}
          onChange={handleChange}
          min="0"
        />
      </div>

      <ResponsiveStack>
        <Button
          type="button"
          loading={isSaving}
          disabled={!hasFieldChanged() || isSaving || isFormEmpty}
          onClick={handleSaveField}
        >
          {isSaving ? "Saving..." : "Save changes"}
        </Button>
        {lastSavedSection === section && <Saved />}
      </ResponsiveStack>
    </div>
  );
}
